import React, { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../../utils/methods';
import {
  previewSubscriptionCancellation,
  cancelSubscription,
  getSubscriptionLogs,
} from '../../../../api/subscription';
import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormSection,
  FormRow,
  PeriodFormLabel,
  ButtonWrapper,
  CancelButton,
  SaveButton,
} from '../../ChangeTFreezyModalStyled';

const ymd = v => (v ? String(v).slice(0, 10).replaceAll('-', '.') : '-');

const BILLING_STATUS_LABEL = {
  ACTIVE: '구독중',
  PAUSED: '일시중지',
  FAILED: '결제 실패',
  CANCELLED: '해지됨',
  NONE: '정기결제 없음',
};

const EVENT_LABEL = {
  REGISTERED: '구독 등록',
  CANCELLED: '해지',
  REACTIVATED: '재구독',
  CARD_CHANGED: '결제수단 변경',
  DATE_SHIFT: '결제일 변경',
};

const ACTOR_LABEL = { MEMBER: '회원(앱)', ADMIN: '관리자', SYSTEM: '자동' };

// 청구 이벤트(REGISTERED 등 감사 코드가 아닌 것)는 성공/실패로 표기
const eventLabel = log =>
  EVENT_LABEL[log.event] ?? (log.success ? '결제 성공' : '결제 실패');

// 구독 해지 모달 — 약정/위약금 없음. 해지 = 정기결제만 중지, 이용권은 만료일까지 유지.
// 대상은 구독제 이용권(COURSE_PASS)뿐.
export default function SubscriptionCancellationModal({ modal, setModal, bigUser, ticket, passType, getAllTickets }) {
  const toast = useToast();
  const token = bigUser?.token;
  const passId = ticket?.memberCoursePassId;
  const productLabel = ticket?.ticketName || ticket?.productName || '-';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);

  const onClose = () => setModal({ ...modal, openSubCancel: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await previewSubscriptionCancellation('COURSE_PASS', passId, token);
        if (alive) setPreview(res);
      } catch (error) {
        if (alive) toast.error(errorMessages(error));
      } finally {
        if (alive) setLoading(false);
      }
      try {
        // 변경/청구 이력 — 분쟁·CS 대응용. 실패해도 해지 기능은 막지 않는다.
        const logRes = await getSubscriptionLogs('COURSE_PASS', passId, token);
        if (alive) setLogs(Array.isArray(logRes) ? logRes : []);
      } catch (error) {
        // 이력 조회 실패는 조용히 무시
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alreadyCancelled = preview?.billingStatus === 'CANCELLED';

  // 재구독은 회원 본인이 회원앱에서만 가능 — 직원이 회원 카드 자동결제를 재개하지 않는다.
  const submit = async () => {
    try {
      setSubmitting(true);
      // 해지: 정기결제만 중지(billing CANCELLED). 이용권은 만료일까지 유지.
      await cancelSubscription('COURSE_PASS', passId, token);
      toast.success('구독이 해지되었습니다. 다음 결제부터 청구되지 않습니다.');
      getAllTickets();
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={modal.openSubCancel} onClose={onClose} aria-labelledby="subscription-cancel-modal">
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>구독 해지</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <FormSection>
            <FormRow>
              <PeriodFormLabel>상품</PeriodFormLabel>
              <span>{productLabel}</span>
            </FormRow>
            {loading ? (
              <FormRow>
                <span>구독 정보를 불러오는 중…</span>
              </FormRow>
            ) : preview ? (
              <>
                <FormRow>
                  <PeriodFormLabel>정기결제 상태</PeriodFormLabel>
                  <span>{BILLING_STATUS_LABEL[preview.billingStatus] ?? preview.billingStatus ?? '-'}</span>
                </FormRow>
                <FormRow>
                  <PeriodFormLabel>이용 가능 기한</PeriodFormLabel>
                  <strong style={{ color: '#0F7F85' }}>{ymd(preview.expireDate)} 까지</strong>
                </FormRow>
                <FormRow>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {alreadyCancelled
                      ? '이미 해지된 구독입니다. 재구독은 회원이 회원앱에서 직접 할 수 있습니다.'
                      : '해지하면 다음 결제부터 청구되지 않으며, 회원은 만료일까지 계속 이용할 수 있습니다. 위약금은 없습니다.'}
                  </span>
                </FormRow>
              </>
            ) : (
              <FormRow>
                <span>구독 정보를 불러오지 못했습니다.</span>
              </FormRow>
            )}

            {logs.length > 0 && (
              <FormRow>
                <div style={{ width: '100%' }}>
                  <PeriodFormLabel>변경 이력</PeriodFormLabel>
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: 'auto',
                      marginTop: 6,
                      border: '1px solid #eee',
                      borderRadius: 6,
                      padding: '6px 10px',
                      fontSize: 12,
                      color: '#555',
                    }}>
                    {logs.map((log, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 8,
                          padding: '3px 0',
                          borderBottom: i < logs.length - 1 ? '1px solid #f4f4f4' : 'none',
                        }}>
                        <span style={{ whiteSpace: 'nowrap', color: '#999' }}>
                          {(log.occurredAt || '').slice(0, 16)}
                        </span>
                        <span style={{ whiteSpace: 'nowrap', fontWeight: 600, color: log.success === false ? '#d9534f' : '#333' }}>
                          {eventLabel(log)}
                        </span>
                        <span style={{ whiteSpace: 'nowrap', color: '#0F7F85' }}>
                          {ACTOR_LABEL[log.actorType] ?? ''}
                        </span>
                        {log.event !== 'REGISTERED' && !EVENT_LABEL[log.event] && log.amount > 0 && (
                          <span style={{ whiteSpace: 'nowrap' }}>{Number(log.amount).toLocaleString()}원</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </FormRow>
            )}
          </FormSection>

          <ButtonWrapper>
            <CancelButton onClick={onClose}>{alreadyCancelled ? '닫기' : '취소'}</CancelButton>
            {!alreadyCancelled && (
              <SaveButton
                isActive={!loading && !!preview && !submitting}
                disabled={loading || !preview || submitting}
                onClick={submit}>
                해지하기
              </SaveButton>
            )}
          </ButtonWrapper>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
}
